"use client";
/**
 * ChatInput — textarea with file attachment, mode selector, send button.
 * Track C2.
 *
 * Exposes setMessage() via forwardRef so parent can inject suggested prompts.
 * Listens for the selectSuggestedPrompt CustomEvent to fill the textarea.
 */
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  type KeyboardEvent,
} from "react";
import {
  Paperclip,
  Send,
  X,
  Loader2,
  ChevronDown,
  Sparkles,
  Zap,
  Square,
} from "lucide-react";
import { useAuthContext } from "@/platform/auth/AuthContext";
import { isPaidProfile } from "@/shared/types";

const DRAFT_KEY = "ask_message";

export interface ChatInputHandle {
  setMessage: (value: string) => void;
  getMessage: () => string;
  submit: () => void;
  focus: () => void;
}

interface ChatInputProps {
  onSend:       (content: string, attachments?: File[], mode?: "fast" | "expert") => Promise<void>;
  onStop?:      () => void;
  isLoading:    boolean;
  disabled?:    boolean;
  sessionId?:   string;
}

const ALLOWED_TYPES = [
  ".pdf", ".docx", ".txt", ".csv", ".md",
  ".jpg", ".jpeg", ".png", ".gif", ".webp",
];
const MAX_FILE_BYTES = 32 * 1024 * 1024; // 32 MB per file (Claude API limit)
const MAX_FILES = 5;
const MAX_TOTAL_BYTES = 30 * 1024 * 1024; // 30 MB aggregate

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  ({ onSend, onStop, isLoading, disabled, sessionId }, ref) => {
    const { profile } = useAuthContext();
    const [message,     setMessage]     = useState("");
    const [attachments, setAttachments] = useState<File[]>([]);
    const [isSubmitting,setIsSubmitting]= useState(false);
    const [mode,        setMode]        = useState<"fast" | "expert">("fast");
    const [modeOpen,    setModeOpen]    = useState(false);
    const [fileError,   setFileError]   = useState<string | null>(null);

    const textareaRef  = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const messageRef   = useRef<string>("");

    // ── Draft persistence ─────────────────────────────────────────────────

    const draftKey = sessionId ? `${DRAFT_KEY}_${sessionId}` : DRAFT_KEY;

    useEffect(() => {
      const saved = localStorage.getItem(draftKey);
      const value = saved ?? "";
      messageRef.current = value;
      setMessage(value);
    }, [draftKey]);

    // ── Textarea auto-grow ────────────────────────────────────────────────

    useEffect(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = message.trim()
        ? Math.min(el.scrollHeight, 200) + "px"
        : "40px";
    }, [message]);

    // ── Expose handle ─────────────────────────────────────────────────────

    useImperativeHandle(ref, () => ({
      setMessage: (value: string) => {
        messageRef.current = value;
        setMessage(value);
        localStorage.setItem(draftKey, value);
        setTimeout(() => {
          const el = textareaRef.current;
          if (!el) return;
          el.style.height = "auto";
          el.style.height = value.trim()
            ? Math.min(el.scrollHeight, 200) + "px"
            : "40px";
        }, 0);
      },
      getMessage: () => messageRef.current,
      submit: () => {
        void handleSubmit();
      },
      focus: () => {
        textareaRef.current?.focus();
      },
    }));

    // ── Listen for suggested prompt event ─────────────────────────────────

    useEffect(() => {
      const handler = (e: Event) => {
        const detail = (e as CustomEvent<{ prompt?: string; text?: string }>).detail;
        const prompt = detail?.prompt ?? detail?.text;
        if (!prompt) return;
        setMessage(prompt);
        messageRef.current = prompt;
        localStorage.setItem(draftKey, prompt);
        textareaRef.current?.focus();
      };
      window.addEventListener("selectSuggestedPrompt", handler);
      return () => window.removeEventListener("selectSuggestedPrompt", handler);
    }, [draftKey]);

    // ── Submit ────────────────────────────────────────────────────────────

    const handleSubmit = useCallback(async () => {
      const message = messageRef.current;
      if ((!message.trim() && attachments.length === 0) || isSubmitting || isLoading) return;

      const content   = message.replace(/\n/g, "<br>");
      const origMsg   = message;
      const origFiles = [...attachments];

      setIsSubmitting(true);
      localStorage.removeItem(draftKey);
      messageRef.current = "";
      setMessage("");
      setAttachments([]);

      try {
        await onSend(content, origFiles, mode);
      } catch {
        // Restore on failure
        messageRef.current = origMsg;
        setMessage(origMsg);
        setAttachments(origFiles);
        localStorage.setItem(draftKey, origMsg);
      } finally {
        setIsSubmitting(false);
      }
    }, [attachments, isSubmitting, isLoading, onSend, draftKey, mode]);

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    // ── File handling ─────────────────────────────────────────────────────

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFileError(null);
      const newFiles = e.target.files ? Array.from(e.target.files) : [];
      if (!fileInputRef.current) return;
      fileInputRef.current.value = "";
      if (newFiles.length === 0) return;

      const currentSub = profile?.subscription as unknown as { plan?: string; tier?: string };
      const currentIsFree = !isPaidProfile(currentSub);
      const currentMaxFiles = currentIsFree ? 1 : MAX_FILES;

      if (attachments.length + newFiles.length > currentMaxFiles) {
        if (currentIsFree) {
          setFileError("Free plan allows 1 file. Upgrade to Pro for 5 files.");
        } else {
          setFileError(`Maximum ${currentMaxFiles} files allowed. You have ${attachments.length} already.`);
        }
        return;
      }

      // Validate each file
      for (const file of newFiles) {
        const ext = "." + file.name.split(".").pop()?.toLowerCase();
        if (!ALLOWED_TYPES.includes(ext)) {
          setFileError(`Invalid file type: ${file.name}. Allowed: ${ALLOWED_TYPES.join(", ")}`);
          return;
        }
        if (file.size > MAX_FILE_BYTES) {
          setFileError(`${file.name} is too large (${(file.size / (1024*1024)).toFixed(1)} MB). Max 32 MB.`);
          return;
        }
      }

      // Validate aggregate size
      const currentTotal = attachments.reduce((sum, f) => sum + f.size, 0);
      const newTotal = newFiles.reduce((sum, f) => sum + f.size, 0);
      if (currentTotal + newTotal > MAX_TOTAL_BYTES) {
        setFileError(`Total file size would exceed 30 MB limit.`);
        return;
      }

      setAttachments((prev) => [...prev, ...newFiles]);
    };

    const removeAttachment = (i: number) =>
      setAttachments((prev) => prev.filter((_, idx) => idx !== i));

    const busy = isSubmitting || isLoading;
    const canSend = (message.trim().length > 0 || attachments.length > 0) && !busy;

    // ── Render ────────────────────────────────────────────────────────────

    return (
      <div className="w-full max-w-4xl mx-auto px-4">
        <div className="relative group">
          {/* Gradient border glow */}
          <div
            className={`absolute -inset-0.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-3xl opacity-0 group-hover:opacity-20 blur-sm transition-all duration-1000 ${
              message.trim() ? "opacity-15" : ""
            }`}
          />

          <div className="relative bg-card border border-border/50 rounded-3xl shadow-lg transition-all duration-300">
            {/* Attachment preview */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 pb-0">
                {attachments.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-xl text-xs border border-border/50"
                  >
                    <div className="flex flex-col">
                      <span className="truncate max-w-[140px] font-medium">{f.name}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {f.size < 1024 * 1024
                          ? `${(f.size / 1024).toFixed(1)} KB`
                          : `${(f.size / (1024 * 1024)).toFixed(1)} MB`}
                      </span>
                    </div>
                    <button
                      onClick={() => removeAttachment(i)}
                      disabled={busy}
                      className="text-muted-foreground hover:text-red-500 transition-colors"
                      aria-label="Remove attachment"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="p-3 md:p-4">
              {/* Textarea */}
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => {
                  messageRef.current = e.target.value;
                  setMessage(e.target.value);
                  localStorage.setItem(draftKey, e.target.value);
                }}
                onKeyDown={handleKeyDown}
                placeholder="Ask about supply chain, procurement, logistics..."
                disabled={disabled}
                rows={1}
                className="w-full resize-none bg-transparent border-0 outline-none focus:outline-none focus:ring-0 text-foreground text-sm sm:text-base leading-6 py-1.5 px-1 min-h-[40px] max-h-[200px] placeholder:text-muted-foreground/70"
                aria-label="Chat message"
              />

              {/* Controls row */}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30">
                {/* Left: attach + mode */}
                <div className="flex items-center gap-2">
                  {/* Attach */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || busy}
                    className="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all flex items-center justify-center"
                    aria-label="Attach file"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    multiple
                    onChange={handleFileSelect}
                    accept={ALLOWED_TYPES.join(",")}
                  />

                  {/* Mode selector */}
                  <div className="relative">
                    <button
                      type="button"
                      disabled={disabled || busy}
                      onClick={() => setModeOpen((o) => !o)}
                      className="h-8 px-3 rounded-lg bg-muted hover:bg-muted/80 border border-border/50 text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-xs font-medium transition-all"
                      aria-haspopup="listbox"
                      aria-expanded={modeOpen}
                    >
                      {mode === "fast" ? (
                        <Zap className="h-3.5 w-3.5" />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5" />
                      )}
                      <span className="hidden sm:inline">
                        {mode === "fast" ? "Fast" : "Expert"}
                      </span>
                      <ChevronDown className="h-3 w-3" />
                    </button>

                    {modeOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setModeOpen(false)}
                        />
                        <div
                          role="listbox"
                          className="absolute left-0 bottom-full mb-2 z-20 w-48 bg-background border border-border/50 rounded-xl shadow-xl p-1"
                        >
                          {(["fast", "expert"] as const).map((m) => (
                            <button
                              key={m}
                              role="option"
                              aria-selected={mode === m}
                              onClick={() => { setMode(m); setModeOpen(false); }}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                                mode === m
                                  ? "bg-indigo-500/10 text-indigo-400"
                                  : "hover:bg-muted text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              {m === "fast" ? (
                                <Zap className="h-4 w-4" />
                              ) : (
                                <Sparkles className="h-4 w-4" />
                              )}
                              <div className="text-left">
                                <div className="font-medium capitalize">{m}</div>
                                <div className="text-[11px] opacity-70">
                                  {m === "fast" ? "Quick responses" : "Deeper analysis"}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Right: Stop (while running) + Send */}
                <div className="flex items-center gap-2">
                  {busy && (
                    <button
                      type="button"
                      onClick={onStop}
                      aria-label={onStop ? "Stop generating" : "Generating…"}
                      title="Stop"
                      disabled={!onStop}
                      className="h-9 px-3 rounded-xl flex items-center gap-1.5 text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-40"
                    >
                      <Square className="h-3.5 w-3.5 fill-current" />
                      <span className="hidden sm:inline">Stop</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSend}
                    className={`h-9 w-9 rounded-xl flex items-center justify-center transition-all duration-300 ${
                      canSend
                        ? "bg-gradient-to-r from-indigo-500 to-pink-500 text-white shadow-lg hover:shadow-indigo-500/30 hover:scale-105 active:scale-95"
                        : "bg-muted text-muted-foreground cursor-not-allowed"
                    }`}
                    aria-label="Send message"
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* File error popup */}
        {fileError && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ animation: "fadeIn 0.15s ease-out" }}>
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setFileError(null)}
              style={{ animation: "fadeIn 0.15s ease-out" }}
            />
            <div
              className="relative bg-card border border-red-500/30 rounded-2xl shadow-2xl shadow-red-500/10 p-6 max-w-md w-full"
              style={{ animation: "popIn 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)" }}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <X className="h-5 w-5 text-red-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground">File Upload Error</h3>
                  <p className="text-sm text-muted-foreground mt-1 break-words">{fileError}</p>
                </div>
              </div>
              {fileError.includes("Free plan") ? (
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => setFileError(null)}
                    className="flex-1 h-9 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors"
                  >
                    Got it
                  </button>
                  <button
                    onClick={() => { window.location.href = "/pricing"; }}
                    className="flex-1 h-9 rounded-xl bg-gradient-to-r from-indigo-500 to-pink-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-indigo-500/20 transition-all"
                  >
                    Upgrade to Pro
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setFileError(null)}
                  className="mt-4 w-full h-9 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-500 text-sm font-medium transition-colors"
                >
                  Got it
                </button>
              )}
            </div>
            <style>{`
              @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
              @keyframes popIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
            `}</style>
          </div>
        )}

        {/* Footer note */}
        <p className="text-center text-xs text-muted-foreground mt-2">
          <span className="bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent font-semibold">
            sensei.
          </span>{" "}
          provides guidance — always verify critical supply chain decisions.
        </p>
      </div>
    );
  }
);

ChatInput.displayName = "ChatInput";
